import type { HTMLAttributes } from 'react';
import { MTextBlockSimpleProps } from '../../molecules/MTextBlockSimple/MTextBlockSimple.types';
import { MButtonProps } from '../../molecules/MButton/MButton.types';
import { MMediaSelectorProps } from '../../molecules/MMediaSelector/MMediaSelector.types';
import { AHeaderTag, type AHeaderProps } from '../../atoms/AHeader/AHeader.types';
import type { TrackingPropsInterface } from '../../utils/enums/enums';

export enum OCardFeatureContentAlignment {
  Left = 'Left',
  Centered = 'Centered',
}

export enum OCardFeatureLayout {
  Default = 'Default',
  Reverse = 'Reverse',
}

export enum OCardFeatureImageScale {
  L = 'L', // aspect-ratio: Desktop: 1:1, Tablet: 1:1, Mobile: 1:1
  M = 'M', // aspect-ratio: Desktop: 3:2, Tablet: 1:1, Mobile: 3:2
  S = 'S', // aspect-ratio: Desktop: 16:9, Tablet: 1:1, Mobile: 16:9
}

/**
 * `imageScale` as authored. Accepts the {@link OCardFeatureImageScale} enum or
 * its raw string equivalent (e.g. `'L'`), since AEM delivers this as a plain
 * string.
 */
export type OCardFeatureImageScaleInput = OCardFeatureImageScale | `${OCardFeatureImageScale}`;

/**
 * `contentAlignment` as authored. Accepts the {@link OCardFeatureContentAlignment}
 * enum or its raw string equivalent (e.g. `'Left'`), since AEM delivers this as
 * a plain string.
 */
export type OCardFeatureContentAlignmentInput = OCardFeatureContentAlignment | `${OCardFeatureContentAlignment}`;

/**
 * `layout` as authored. Accepts the {@link OCardFeatureLayout} enum or its raw
 * string equivalent (e.g. `'Default'`), since AEM delivers this as a plain
 * string.
 */
export type OCardFeatureLayoutInput = OCardFeatureLayout | `${OCardFeatureLayout}`;

type AEMBoolean = 'true' | 'false';

/**
 * `aHeader` as authored. Accepts AHeaderTag or the raw, and uppercase
 * form AEM delivers (e.g. `'H2'`).
 */
export interface OCardFeatureAHeaderInput extends Partial<Omit<AHeaderProps, 'headerTag'>> {
  headerTag?: AHeaderTag | `${AHeaderTag}` | Uppercase<`${AHeaderTag}`>;
}

/**
 * OCardFeature accepts only the nested MTextBlockSimple props
 * (`aEyebrow`, `aHeader`, `aBody`) — the flat `eyebrowText`/`headerText`/
 * `bodyText` fields are intentionally not supported here. Alignment is driven
 * by the organism's `contentAlignment`, so it is not exposed on this shape.
 * `aHeader` accepts the AEM-authored tag form.
 */
export interface OCardFeatureTextBlockProps {
  aEyebrow?: MTextBlockSimpleProps['aEyebrow'];
  aHeader?: OCardFeatureAHeaderInput;
  aBody?: MTextBlockSimpleProps['aBody'];
}

export interface OCardFeatureProps extends HTMLAttributes<HTMLElement> {
  contentAlignment?: OCardFeatureContentAlignmentInput;
  layout?: OCardFeatureLayoutInput;
  button1?: AEMBoolean;
  button2?: AEMBoolean;
  imageScale?: OCardFeatureImageScaleInput;
  mTextBlockSimple?: OCardFeatureTextBlockProps;
  mButton?: MButtonProps;
  mButton2?: MButtonProps;
  mMediaSelector?: MMediaSelectorProps;
  trackingProperties?: TrackingPropsInterface;
}
